/**
 * Global protection against accidental modal closure when selecting text or dragging.
 *
 * Problem:
 * When a user selects text or drags inside a modal and the mouse/pointer
 * release happens outside the modal on the backdrop overlay, the browser generates
 * a synthetic 'click' event on the common ancestor (the backdrop overlay).
 * If the backdrop overlay listens to onClick to close the modal (e.g. `e.target === e.currentTarget && onClose()`),
 * this closes the modal unexpectedly and causes users to lose their input data.
 *
 * Solution:
 * Track the target of mousedown/pointerdown/touchstart.
 * If a 'click' event occurs on a modal backdrop, but the mousedown did NOT originate
 * on the backdrop itself (e.g. started inside the modal dialog), cancel the synthetic
 * click event in the capturing phase before any React or DOM event listeners run.
 */

export function isModalBackdrop(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false
  if (el === document.body || el === document.documentElement) return false

  // 1. Direct class checks for common modals
  if (
    el.classList.contains('modal') ||
    el.classList.contains('modal-backdrop') ||
    el.classList.contains('confirm-overlay')
  ) {
    return true
  }

  // 2. Fixed or absolute fullscreen overlay styling (e.g. WRAP_STYLE in ShipsEdge)
  try {
    const style = window.getComputedStyle(el)
    const isFixedOrAbsolute = style.position === 'fixed' || style.position === 'absolute'
    const zIndex = parseInt(style.zIndex, 10)
    const hasModalZIndex = !isNaN(zIndex) && zIndex >= 100

    if (isFixedOrAbsolute && hasModalZIndex) {
      const rect = el.getBoundingClientRect()
      const coversViewport =
        rect.width >= window.innerWidth * 0.75 && rect.height >= window.innerHeight * 0.75
      if (coversViewport) {
        return true
      }
    }
  } catch (err) {
    // Ignore any style reading errors
  }

  return false
}

export function initBackdropSwipeProtection() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  let lastDownTarget: EventTarget | null = null
  let isDownOnBackdrop = false

  const handlePointerDown = (e: MouseEvent | TouchEvent | PointerEvent) => {
    lastDownTarget = e.target
    isDownOnBackdrop = isModalBackdrop(e.target)
  }

  const handleClickCapture = (e: MouseEvent) => {
    const target = e.target
    if (isModalBackdrop(target)) {
      // If the mousedown did NOT start on this backdrop element, or started on a child inside the modal:
      const startedOnSameBackdrop = isDownOnBackdrop && lastDownTarget === target
      if (!startedOnSameBackdrop) {
        // Prevent the synthetic click from closing the modal
        e.stopPropagation()
        e.stopImmediatePropagation()
        e.preventDefault()
      }
    }
  }

  document.addEventListener('mousedown', handlePointerDown, true)
  document.addEventListener('pointerdown', handlePointerDown, true)
  document.addEventListener('touchstart', handlePointerDown, true)
  document.addEventListener('click', handleClickCapture, true)
}
